import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, EmptyState, Inline, Panel, Skeleton, Stack, t, Text } from '@kanbios/ui';
import type { provisioning } from '@kanbios/api-client';
import { api, unwrap } from '../lib/api';
import { relativeTime, shortDateTime } from '../lib/format';
import { AccessCheckStatus, useAccessCheck } from './AccessCheck';
import { BootstrapDrawer } from './BootstrapDrawer';
import { PendingButton } from './PendingButton';
import { EnrolmentDrawer } from '../pages/settings/EnrolmentDrawer';
import { explainAccess } from '../lib/accessHint';
import { ListTable } from './ListTable';

type Configuration = provisioning.components['schemas']['ServerConfiguration'];
type Component = Configuration['components'][number];
type Job = Configuration['jobs'][number];

const ACCESS_TONES: Record<string, 'pos' | 'warn' | 'neg' | 'neutral'> = { trusted: 'pos', no_sudo: 'warn', not_trusted: 'warn', unreachable: 'neg', not_applicable: 'neutral', unknown: 'neutral' };
const STATE_TONES = { applied: 'pos', outdated: 'warn', missing: 'neutral' } as const;
const JOB_TONES: Record<string, 'pos' | 'warn' | 'neg' | 'info' | 'neutral'> = { succeeded: 'pos', partial: 'warn', failed: 'neg', running: 'info', queued: 'neutral' };

/** « 4 min 12 s » between two instants. */
function elapsed(from: string, to: string): string {
  const seconds = Math.max(0, Math.round((Date.parse(to) - Date.parse(from)) / 1000));
  const minutes = Math.floor(seconds / 60);
  return minutes ? `${minutes} min ${String(seconds % 60).padStart(2, '0')} s` : `${seconds} s`;
}

export const configurationKey = (server: string) => ['server-configuration', server];

/** The server's configuration (D30), shared by the tab and the header action. */
export function useServerConfiguration(server: string, enabled = true) {
  return useQuery({
    queryKey: configurationKey(server),
    queryFn: async () => unwrap(await api.provisioning.GET('/servers/{server}/configuration', { params: { path: { server } } })),
    enabled,
    // D38: an enrolment waiting or running is followed in the page's band.
    refetchInterval: query => (query.state.data?.enrolment?.state === 'running' || query.state.data?.jobs.some(j => j.status === 'queued' || j.status === 'running') ? 5_000 : 60_000),
  });
}

/**
 * D30, the platform side of a server on its own page: whether it trusts the
 * platform (with « Amorcer » and « Vérifier l'accès »), a checklist of what
 * the enrolment put on it — each part applied, with a newer version waiting,
 * or never applied — and its enrolments with their follow-up.
 */
export function ConfigurationPanel({ server }: { server: string }) {
  const navigate = useNavigate();
  const [bootstrapping, setBootstrapping] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const config = useServerConfiguration(server);
  const check = useAccessCheck();
  const c = config.data;
  if (config.isPending) return <Skeleton lines={8} />;
  if (!c) return <EmptyState icon="error" title={t('configuration.failed')} />;
  const access = c.access;
  const trusted = access?.state === 'trusted';
  const changes = (k: Component) => k.pending.map(v => t(`configuration.change.${k.id}.${v}`));

  return (
    <Stack gap={24}>
      <Panel
        title={t('configuration.access.title')}
        subtitle={access?.checkedAt ? t('configuration.access.checked', { when: relativeTime(access.checkedAt) }) : t('configuration.access.never')}
        actions={c.canCheck ? (
          <Inline gap={8}>
            {!trusted && <Button variant="secondary" size="sm" icon="add_link" onClick={() => setBootstrapping(true)}>{t('configuration.access.bootstrap')}</Button>}
            <PendingButton variant="ghost" size="sm" icon="sync" pending={check.running} onClick={check.run}>{t('configuration.access.check')}</PendingButton>
          </Inline>
        ) : undefined}
      >
        <Stack gap={8}>
          <Inline><Badge tone={ACCESS_TONES[access?.state ?? 'unknown'] ?? 'neutral'}>{t(`settings.access.states.${access?.state ?? 'unknown'}`)}</Badge></Inline>
          <Text variant="body-sm" tone="mute">{trusted ? t('configuration.access.trustedBody') : explainAccess(access?.detail) || t('configuration.access.notTrustedBody')}</Text>
          <AccessCheckStatus feedback={check.feedback} />
        </Stack>
      </Panel>

      <Panel
        title={t('configuration.checklist.title')}
        subtitle={c.enrolled
          ? `${t('configuration.checklist.lastEnrolment', { when: shortDateTime(c.enrolment!.enrolledAt!) })}${c.pending ? ` · ${t('configuration.checklist.pending', { n: c.pending })}` : ` · ${t('configuration.checklist.upToDate')}`}`
          : t('configuration.checklist.neverEnrolled')}
        // Before D30 the versions are read from the date of the last enrolment.
        footer={c.enrolled && !c.recorded ? <Text variant="caption" tone="mute">{t('configuration.checklist.inferred')}</Text> : undefined}
        actions={c.canEnrol ? (
          <Button variant={c.pending ? 'primary' : 'secondary'} size="sm" icon="system_update_alt" onClick={() => navigate(`/servers/import?servers=${encodeURIComponent(server)}`)}>
            {c.enrolled ? t('configuration.update') : t('configuration.enrol')}
          </Button>
        ) : undefined}
        flush
      >
        <ListTable<Component>
          label={t('configuration.checklist.title')}
          queryKey={[...configurationKey(server), 'components']}
          fetch={async query => unwrap(await api.provisioning.GET('/servers/{server}/configuration/components', { params: { path: { server }, query: query as never } }))}
          rowKey="id"
          defaultSort={{ key: 'order', dir: 'asc' }}
          search={t('configuration.search')}
          facets={[{ key: 'state', label: t('configuration.columns.state'), valueLabel: v => t(`configuration.state.${v}`) }]}
          refetchInterval={false}
          // One idea per column: the part, its state, the versions, what an update brings, then what the part does.
          columns={[
            { key: 'id', label: t('configuration.columns.part'), pinned: true, width: 220, strong: true, sortable: false, accessor: (k: Component) => t(`configuration.component.${k.id}`) },
            { key: 'state', label: t('configuration.columns.state'), width: 130, accessor: (k: Component) => k.state, render: (k: Component) => <Badge tone={STATE_TONES[k.state]} size="sm">{t(`configuration.state.${k.state}`)}</Badge> },
            { key: 'applied', label: t('configuration.columns.applied'), width: 90, align: 'right', mono: true, accessor: (k: Component) => (k.applied ? t('configuration.version', { v: k.applied }) : '—') },
            { key: 'current', label: t('configuration.columns.current'), width: 100, align: 'right', mono: true, accessor: (k: Component) => t('configuration.version', { v: k.current }) },
            { key: 'pending', label: t('configuration.columns.changes'), grow: true, width: 280, sortable: false, accessor: (k: Component) => (k.state === 'applied' ? '—' : changes(k).join(' · ')) },
            { key: 'role', label: t('configuration.columns.role'), width: 280, sortable: false, accessor: (k: Component) => t(`configuration.component.${k.id}.what`) },
          ]}
          empty={<EmptyState framed={false} icon="checklist" title={t('configuration.checklist.neverEnrolled')} />}
        />
      </Panel>

      <Panel title={t('configuration.jobs.title')} subtitle={t('configuration.jobs.subtitle')} flush>
        <ListTable<Job>
          label={t('configuration.jobs.title')}
          queryKey={[...configurationKey(server), 'jobs']}
          fetch={async query => unwrap(await api.provisioning.GET('/servers/{server}/configuration/jobs', { params: { path: { server }, query: query as never } }))}
          rowKey="id"
          defaultSort={{ key: 'createdAt', dir: 'desc' }}
          search={t('configuration.jobs.search')}
          facets={[{ key: 'status', label: t('configuration.jobs.status'), valueLabel: v => t(`configuration.jobs.states.${v}`) }]}
          onRowClick={(j: Job) => setJobId(j.id)}
          columns={[
            { key: 'createdAt', label: t('configuration.jobs.when'), width: 150, render: (j: Job) => shortDateTime(j.createdAt) },
            { key: 'status', label: t('configuration.jobs.status'), width: 130, accessor: (j: Job) => j.status, render: (j: Job) => <Badge tone={JOB_TONES[j.status] ?? 'neutral'} size="sm">{t(`configuration.jobs.states.${j.status}`)}</Badge> },
            { key: 'duration', label: t('configuration.jobs.duration'), width: 110, align: 'right', sortable: false, accessor: (j: Job) => (j.finishedAt ? elapsed(j.createdAt, j.finishedAt) : '—') },
            { key: 'finishedAt', label: t('configuration.jobs.finished'), width: 150, render: (j: Job) => (j.finishedAt ? shortDateTime(j.finishedAt) : '—') },
            { key: 'actor', label: t('configuration.jobs.actor'), grow: true, width: 200, sortable: false },
          ]}
          empty={<EmptyState framed={false} icon="rocket_launch" title={t('configuration.jobs.empty')} />}
        />
      </Panel>

      <BootstrapDrawer open={bootstrapping} onClose={() => setBootstrapping(false)} server={server} onCheck={check.run} checking={check.running} />
      <EnrolmentDrawer
        jobId={jobId}
        onClose={() => setJobId(null)}
        canRetry={c.canEnrol}
        onRetry={failed => navigate(`/servers/import?servers=${encodeURIComponent(failed.join(','))}`)}
      />
    </Stack>
  );
}
